import { preferenceLanguages } from '@socialgoodsoftware/libraries/models/tables/preference-values';
import { preferencePageSizes } from '@socialgoodsoftware/libraries/models/tables/preference-values';
import { preferenceThemes } from '@socialgoodsoftware/libraries/models/tables/preference-values';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const contract = initContract();

const errorSchema = z.object({
  message: z.string(),
});

const themeSchema = z.enum(preferenceThemes, { message: 'Choose the default, light, dark or accessible theme' });

const pageSizeSchema = z.literal(preferencePageSizes, { message: 'Choose 5, 10, 30, 50 or 100 rows per page' });

const languageSchema = z.enum(preferenceLanguages, { message: 'Choose one of the supported languages' });

const preferencesSchema = z.object({
  fullWidth: z.boolean().optional(),
  theme: themeSchema.optional(),
  language: languageSchema.optional(),
  pageSize: pageSizeSchema.optional(),
});

const preferencesViewSchema = z.object({
  fullWidth: z.boolean(),
  theme: themeSchema,
  language: languageSchema,
  pageSize: pageSizeSchema,
});

export const preferencesRoutes = contract.router({
  getPreferences: {
    method: 'GET',
    metadata: { access: { requiresUser: true } },
    path: '/api/preferences',
    responses: {
      200: preferencesViewSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: 'Read the current user preferences',
    description:
      'Returns the five user-settable preferences — nav collapse, full-width layout, theme, language and page size — for the signed-in user from the organization preferences table, creating defaults on first read. A stored value outside the supported vocabulary is answered as the default. The row also holds dashboardHiddenWidgets, dashboardWidgetLayout and tableStates, which stay internal state and never reach the wire.',
  },
  updatePreferences: {
    method: 'PATCH',
    metadata: { access: { requiresUser: true } },
    path: '/api/preferences',
    body: preferencesSchema,
    responses: {
      200: preferencesViewSchema,
      400: errorSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: 'Update user preferences',
    description:
      'Merges the supplied preference fields over the stored preferences row for the signed-in user and persists the result in the organization preferences table. Only known preference fields are accepted; the response carries the same five user-settable fields as the read endpoint.',
  },
});

export type UpdatePreferencesBody = z.infer<typeof preferencesSchema>;
export type PreferencesView = z.infer<typeof preferencesViewSchema>;
